import * as React from "react"
import { DayPicker } from "react-day-picker"
import { buttonVariants } from "./button"
import 'react-day-picker/style.css'

import { cn } from "@/core/utils"

export type CalendarProps = React.ComponentProps<typeof DayPicker>

const Calendar = React.memo(function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  // Memoize classNames object for react-day-picker v9
  const mergedClassNames = React.useMemo(
    () => ({
      // Layout
      months: "relative flex flex-col sm:flex-row gap-4",
      month: "w-full",
      month_caption: "relative flex justify-center items-center h-10 mb-2",
      caption_label: "text-sm font-semibold flex ",
      // Navigation - using z-index to ensure buttons are clickable
      nav: "absolute inset-x-1 top-0 flex items-center justify-between h-10 ",
      button_previous: cn(
        buttonVariants({ variant: "outline" }),
        "h-7 w-7 bg-background p-0 hover:bg-accent hover:text-accent-foreground cursor-pointer z-10"
      ),
      button_next: cn(
        buttonVariants({ variant: "outline" }),
        "h-7 w-7 bg-background p-0 hover:bg-accent hover:text-accent-foreground cursor-pointer z-10"
      ),
      chevron: "h-4 w-4",

      // Week grid
      month_grid: "w-full border-collapse",
      weekdays: "flex",
      weekday: "text-muted-foreground rounded-md w-9 font-medium text-[0.8rem] text-center",
      week: "flex w-full mt-2 h-full",

      // Days
      day: "h-full w-full text-center text-sm p-0 relative",
      day_button: cn(
        buttonVariants({ variant: "ghost" }),
        "aspect-square w-full p-0 font-normal transition-colors hover:bg-accent hover:text-accent-foreground",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      ),

      // Day states
      selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground rounded-md",
      today: "bg-accent text-accent-foreground font-semibold rounded-md",
      outside: "text-muted-foreground opacity-50",
      disabled: "text-muted-foreground opacity-50 cursor-not-allowed",
      hidden: "invisible",

      // Range mode
      range_start: "rounded-l-md",
      range_end: "rounded-r-md",
      range_middle: "bg-accent text-accent-foreground",

      ...classNames,
    }),
    [classNames]
  )

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={mergedClassNames}
      captionLayout="dropdown"
      endMonth={new Date(new Date().getFullYear() + 1, 11, 31)}
      {...props}
    />
  )
})

Calendar.displayName = "Calendar"

export { Calendar }
